import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  TreeDeciduous, 
  MapPin,
  Award,
  Download,
  Share2,
  Trees,
  Loader2
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

export default function AdoptSuccessPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking');
  const [tree, setTree] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sid}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to check status');
      }

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        // Fetch tree and certificate details
        const treeId = data.metadata?.tree_id;
        if (treeId && treeId !== 'random') {
          await fetchTreeAndCertificate(treeId);
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      pollCount.current = attempts + 1;
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const fetchTreeAndCertificate = async (treeId) => {
    try {
      const [treeRes, certRes] = await Promise.all([
        fetch(`${API}/trees/${treeId}`),
        fetch(`${API}/certificates/${treeId}`)
      ]);

      if (treeRes.ok) {
        const treeData = await treeRes.json();
        setTree(treeData);
      }
      
      if (certRes.ok) {
        const certData = await certRes.json();
        setCertificate(certData.certificate);
      }
    } catch (e) {
      console.error('Failed to fetch details:', e);
    }
  };

  const handleShare = () => {
    const url = tree 
      ? `${window.location.origin}/tree/${tree.tree_id}`
      : window.location.origin;
    
    if (navigator.share) {
      navigator.share({
        title: 'I adopted a tree at Epuyén Renace!',
        text: 'Join me in restoring Patagonia\'s forests.',
        url
      });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background" data-testid="adopt-success-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto text-center">
            <Loader2 className="w-16 h-16 mx-auto animate-spin text-primary" />
            <h2 className="text-2xl font-bold mt-6">Verifying Payment...</h2>
            <p className="text-muted-foreground mt-2">Please wait while we confirm your donation.</p>
          </div>
        </main>
      </div>
    );
  }

  if (status === 'timeout' || status === 'expired') {
    return (
      <div className="min-h-screen bg-background" data-testid="adopt-success-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <TreeDeciduous className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold mt-6">Payment Issue</h2>
            <p className="text-muted-foreground mt-2">
              {status === 'expired' 
                ? 'Your payment session has expired.' 
                : 'We couldn\'t verify your payment. Please check your email for confirmation.'}
            </p>
            <Button asChild className="mt-6">
              <Link to="/adopt">Try Again</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="adopt-success-page">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Animation */}
          <motion.div 
            className="text-center mb-12"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
          >
            <div className="w-24 h-24 mx-auto rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mb-6 animate-pulse-green">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="success-title">
              {t('adopt.successTitle')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('adopt.successMessage')}
            </p>
          </motion.div>

          {/* Tree Details Card */}
          {tree && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="mb-6 overflow-hidden">
                <div className="h-48 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <TreeDeciduous className="w-24 h-24 text-primary/60" />
                </div>
                <CardContent className="p-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Tree ID</p>
                      <p className="font-mono font-semibold">{tree.tree_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Species</p>
                      <p className="font-semibold capitalize">{tree.species}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Coordinates</p>
                      <p className="font-mono text-sm">
                        {tree.lat.toFixed(6)}, {tree.lng.toFixed(6)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="font-semibold capitalize">{tree.status}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Certificate Card */}
          {certificate && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-accent" />
                    Adoption Certificate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-muted rounded-lg border border-dashed border-border">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">Certificate Hash</p>
                      <p className="font-mono text-xs break-all">{certificate.hash}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    This hash verifies your adoption on our public ledger
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Button asChild className="flex-1" data-testid="view-my-forest">
              <Link to="/my-forest">
                <Trees className="w-4 h-4 mr-2" />
                {t('adopt.viewMyCertificate')}
              </Link>
            </Button>
            {tree && (
              <Button asChild variant="outline" className="flex-1">
                <Link to={`/tree/${tree.tree_id}`}>
                  <MapPin className="w-4 h-4 mr-2" />
                  View on Map
                </Link>
              </Button>
            )}
            <Button variant="secondary" onClick={handleShare} data-testid="share-btn">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
